import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { format, subDays, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { CalendarIcon, Users, Clock, Phone, ChevronLeft, ChevronRight, Image, Crown, X } from 'lucide-react';

const STATUS_STYLES = {
  confirmed: { label: 'Confirmed', cls: 'bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20' },
  arrived: { label: 'Arrived', cls: 'bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] border-[color:var(--color-primary)]/20' },
  cancelled: { label: 'Cancelled', cls: 'bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20' },
  'no-show': { label: 'No-show', cls: 'bg-[#D4A017]/10 text-[#D4A017] border-[#D4A017]/20' },
  rescheduled: { label: 'Rescheduled', cls: 'bg-[#6366F1]/10 text-[#6366F1] border-[#6366F1]/20' },
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function AdminReservations() {
  const { getHeaders, API } = useAuth();
  const [tab, setTab] = useState('daily');
  const [date, setDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [filterOutlet, setFilterOutlet] = useState('all');
  const [dailyData, setDailyData] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [calMonth, setCalMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [imageModal, setImageModal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/outlets`, { headers: getHeaders() })
      .then(res => setOutlets(res.data))
      .catch(() => {});
  }, [API, getHeaders]);

  const loadDaily = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/reservations?date=${format(date, 'yyyy-MM-dd')}`;
      if (filterOutlet !== 'all') url += `&outlet_id=${filterOutlet}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setDailyData(res.data);
    } catch { toast.error('Failed to load reservations'); }
    finally { setLoading(false); }
  }, [API, getHeaders, date, filterOutlet]);

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const y = calMonth.getFullYear();
      const m = calMonth.getMonth() + 1;
      let url = `${API}/admin/reservations/calendar?year=${y}&month=${m}`;
      if (filterOutlet !== 'all') url += `&outlet_id=${filterOutlet}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setCalendarData(res.data.dates || {});
    } catch { toast.error('Failed to load calendar'); }
    finally { setLoading(false); }
  }, [API, getHeaders, calMonth, filterOutlet]);

  useEffect(() => { if (tab === 'daily') loadDaily(); }, [tab, loadDaily]);
  useEffect(() => { if (tab === 'future') loadCalendar(); }, [tab, loadCalendar]);

  const outletName = (id) => outlets.find(o => o.id === id)?.name || id;

  const monthStart = startOfMonth(calMonth);
  const monthEnd = endOfMonth(calMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPad = getDay(monthStart);

  return (
    <div data-testid="admin-reservations-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-[#1A2421]">Reservations</h2>
          <p className="text-sm text-[#5C6B64] mt-1">Table bookings & events across outlets (read-only)</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <TabsList className="bg-[#F3F5F2] p-0.5 rounded-lg w-fit">
            <TabsTrigger value="daily" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4"
              data-testid="res-daily-tab">Daily</TabsTrigger>
            <TabsTrigger value="future" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4"
              data-testid="res-future-tab">Calendar</TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filterOutlet} onValueChange={setFilterOutlet}>
              <SelectTrigger className="w-36 h-8 border-[#E2E8DF] text-sm" data-testid="res-outlet-filter">
                <SelectValue placeholder="All Outlets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outlets</SelectItem>
                {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>

            {tab === 'daily' && (
              <>
                <Button variant="outline" size="sm"
                  onClick={() => setDate(subDays(new Date(), 1))}
                  className={`h-8 text-xs border-[#E2E8DF] ${
                    format(date, 'yyyy-MM-dd') === format(subDays(new Date(), 1), 'yyyy-MM-dd')
                      ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]' : 'text-[#5C6B64]'
                  }`}
                  data-testid="res-yesterday-btn">Yesterday</Button>
                <Button variant="outline" size="sm"
                  onClick={() => setDate(new Date())}
                  className={`h-8 text-xs border-[#E2E8DF] ${
                    format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
                      ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]' : 'text-[#5C6B64]'
                  }`}
                  data-testid="res-today-btn">Today</Button>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs border-[#E2E8DF] text-[#5C6B64] gap-1"
                      data-testid="res-date-picker">
                      <CalendarIcon className="w-3.5 h-3.5" />{format(date, 'dd MMM yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar mode="single" selected={date}
                      onSelect={d => { if (d) { setDate(d); setCalendarOpen(false); } }} />
                  </PopoverContent>
                </Popover>
              </>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
          </div>
        )}

        {/* DAILY VIEW - READ ONLY */}
        <TabsContent value="daily">
          {!loading && (
            <Card className="border-[#E2E8DF] shadow-sm overflow-hidden">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-[#1A2421]">{format(date, 'EEEE, dd MMM yyyy')}</CardTitle>
                  <Badge variant="outline" className="border-[#E2E8DF] text-[#5C6B64] text-xs">
                    {dailyData.length} booking{dailyData.length !== 1 ? 's' : ''} · {dailyData.reduce((s, r) => s + (r.pax || 0), 0)} pax
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {dailyData.length === 0 ? (
                  <p className="text-sm text-[#5C6B64] text-center py-8">No reservations for this date</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-[#1A2421]">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-[0.05em] text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                          <th className="px-4 py-2.5 font-medium">Outlet</th>
                          <th className="px-4 py-2.5 font-medium">Time</th>
                          <th className="px-4 py-2.5 font-medium">Guest</th>
                          <th className="px-4 py-2.5 font-medium text-center">Pax</th>
                          <th className="px-4 py-2.5 font-medium">Type</th>
                          <th className="px-4 py-2.5 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dailyData.map((r, i) => {
                          const st = STATUS_STYLES[r.status] || STATUS_STYLES.confirmed;
                          return (
                            <tr key={r.id}
                              className={`border-b border-[#E2E8DF] ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}
                              data-testid={`admin-res-row-${r.id}`}>
                              <td className="px-4 py-2.5 font-medium whitespace-nowrap">{outletName(r.outlet_id)}</td>
                              <td className="px-4 py-2.5 whitespace-nowrap">
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-[#5C6B64]" />{r.time}</span>
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1.5">
                                  <span>{r.guest_name}</span>
                                  {r.is_member && (
                                    <Crown className="w-3 h-3 text-[#D4A017] shrink-0" />
                                  )}
                                </div>
                                {r.mobile && <span className="text-[10px] text-[#5C6B64] flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{r.mobile}</span>}
                              </td>
                              <td className="px-4 py-2.5 text-center font-semibold">{r.pax}</td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1.5">
                                  {r.reservation_type === 'event' ? (
                                    <Badge variant="outline" className="text-[10px] border-[#C45A41]/30 text-[#C45A41]">
                                      {r.event_type || 'Event'}
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-[#5C6B64]">Table</span>
                                  )}
                                  {r.reservation_type === 'event' && r.has_image && (
                                    <button
                                      onClick={() => setImageModal(r.id)}
                                      className="p-1 rounded hover:bg-[#F3F5F2] text-[#C45A41] transition-colors"
                                      title="View event image"
                                      data-testid={`res-view-image-${r.id}`}>
                                      <Image className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2.5">
                                <Badge className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* CALENDAR VIEW */}
        <TabsContent value="future">
          {!loading && (
            <Card className="border-[#E2E8DF] shadow-sm">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#5C6B64]"
                    onClick={() => setCalMonth(subMonths(calMonth, 1))} data-testid="cal-prev-month">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <CardTitle className="text-sm text-[#1A2421]">{format(calMonth, 'MMMM yyyy')}</CardTitle>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#5C6B64]"
                    onClick={() => setCalMonth(addMonths(calMonth, 1))} data-testid="cal-next-month">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-2 pb-3">
                <div className="grid grid-cols-7 mb-1">
                  {WEEKDAYS.map(d => (
                    <div key={d} className="text-center text-[10px] uppercase text-[#5C6B64] font-medium py-1">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: startPad }).map((_, i) => (
                    <div key={`pad-${i}`} className="aspect-square" />
                  ))}
                  {days.map(day => {
                    const key = format(day, 'yyyy-MM-dd');
                    const info = calendarData[key];
                    const isToday = key === format(new Date(), 'yyyy-MM-dd');
                    return (
                      <div key={key}
                        className={`aspect-square p-1 rounded-lg border transition-colors cursor-pointer hover:border-[color:var(--color-primary)]/30 ${
                          isToday ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/5' : 'border-[#E2E8DF]'
                        } ${info ? 'bg-[#F9F9F8]' : ''}`}
                        onClick={() => { setDate(day); setTab('daily'); }}
                        data-testid={`cal-day-${key}`}>
                        <div className={`text-xs font-medium mb-0.5 ${isToday ? 'text-[color:var(--color-primary)]' : 'text-[#1A2421]'}`}>
                          {format(day, 'd')}
                        </div>
                        {info && (
                          <div className="space-y-0.5">
                            {info.tables > 0 && (
                              <div className="flex items-center gap-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#2D6A4F] shrink-0" />
                                <span className="text-[8px] sm:text-[9px] text-[#5C6B64] truncate leading-tight">
                                  {info.tables}b · {info.table_pax}p
                                </span>
                              </div>
                            )}
                            {info.events > 0 && (
                              <div className="flex items-center gap-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#C45A41] shrink-0" />
                                <span className="text-[8px] sm:text-[9px] text-[#5C6B64] truncate leading-tight">
                                  {info.events}e · {info.event_pax}p
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-3 pt-2 border-t border-[#E2E8DF] justify-center">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#2D6A4F]" />
                    <span className="text-[10px] text-[#5C6B64]">Table bookings</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#C45A41]" />
                    <span className="text-[10px] text-[#5C6B64]">Events</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Event Image Modal */}
      <Dialog open={!!imageModal} onOpenChange={(open) => { if (!open) setImageModal(null); }}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden bg-black/95 border-none">
          <div className="relative">
            <button
              onClick={() => setImageModal(null)}
              className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              data-testid="image-modal-close">
              <X className="w-5 h-5" />
            </button>
            {imageModal && (
              <img
                src={`${API}/reservations/${imageModal}/image`}
                alt="Event reference"
                className="w-full max-h-[80vh] object-contain"
                data-testid="image-modal-img"
                onError={(e) => {
                  // iter180c — replaced innerHTML with safe DOM API. The
                  // message is a static literal but the static-analyzer
                  // (correctly) flags innerHTML as an XSS-prone pattern.
                  // Use textContent on a fresh <p> child instead.
                  e.target.style.display = 'none';
                  const parent = e.target.parentElement;
                  if (parent) {
                    parent.textContent = '';
                    const p = document.createElement('p');
                    p.className = 'text-white text-center py-12';
                    p.textContent = 'Image could not be loaded';
                    parent.appendChild(p);
                  }
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
